import * as React from "react";
import {
    getGridSingleSelectOperators,
    GridColumns,
    GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import CclPersistentDataGrid from "../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { updateProductSaleGridState } from "../../../app/slices/productSaleListTableStateSlice";
import { useSelector } from "react-redux";
import { ProductSaleParticipant } from "../../../services/types/enterpriseParticipantApiTypes";
import { IconButton, Tooltip } from "@mui/material";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { addCustomDataGridFilters } from "../../../components/common/customDataGridFilters/customDataGridFilters";
import CclCommonLink from "../../../components/common/cclCommonLink";
import CclDownloadWarningAlert from "../../../components/common/cclDownloadWarningAlert";

export interface ProductSaleAssessmentListProps {
    productSales: ProductSaleParticipant[];
    loading: boolean;
    currentEvent: string;
    downloadReports: (rows?: any[]) => any;
    activateCompass: (rows?: any[]) => any;
    changeFacilitator: (rows?: any[]) => any;
    viewCompass: (imkey: number) => void;
}

export const ProductSaleAssessmentList: React.FC<ProductSaleAssessmentListProps> = (props) => {
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const claimsService = new UserClaimsService();
    const stateSelector = useSelector((state: any) => state.productSaleListTableState);
    const isUserProductSaleManger = claimsService.IsUserInAppRole(2); // check for product sale manager role

    const COLUMNS = [
        {
            field: "fullName",
            headerName: "Participant",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/user?email=${encodeURIComponent(params.row.emailAddress)}&imkey=${
                        params.row.imKey
                    }`}
                />
            ),
            flex: 0.4,
        },
        {
            field: "emailAddress",
            headerName: "Participant Email",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            flex: 0.75,
        },
        {
            field: "facilitatorName",
            headerName: "Facilitator",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/user?email=${encodeURIComponent(params.row.facilitatorEmail)}&imkey=${
                        params.row.facilitatorImKey
                    }`}
                />
            ),
            flex: 0.4,
        },
        {
            field: "facilitatorEmail",
            headerName: "Facilitator Email",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            flex: 0.75,
        },
        {
            field: "assessmentType",
            headerName: "Assessment",
            type: "string",
            flex: 0.75,
        },
        {
            field: "sessionTitle",
            headerName: "Session Title",
            type: "string",
            renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return null;
                return (
                    <CclCommonLink
                        text={params.value}
                        keyVal={params.value}
                        to={`/session/${params.row.sessionEsKey}`}
                    />
                );
            },
            flex: 0.75,
        },
        {
            field: "scoreDate",
            headerName: "Scored",
            valueGetter: (params: any) => new Date(params.row.scoreDate),
            type: "date",
            renderCell: (params: GridRenderCellParams) => (
                <span>{params.value.toLocaleDateString()}</span>
            ),
            width: 150,
        },
        {
            field: "isReleasedToCompass",
            headerName: "Released (Compass)",
            valueOptions: [
                { value: true, label: "Yes" },
                { value: false, label: "No" },
            ],
            filterOperators: [...getGridSingleSelectOperators().filter((op) => op.value === "is")],
            renderCell: (params: GridRenderCellParams) => (
                <span>
                    {isUserProductSaleManger ? (
                        <Tooltip title="View In Compass">
                            <IconButton
                                size="small"
                                onClick={() => props.viewCompass(params.row.imKey)}
                                color="primary"
                                sx={{ pr: 2, pl: 2 }}
                            >
                                <ExploreOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    {params.value ? "Yes" : "No"}
                </span>
            ),
            width: 150,
        },
    ];

    const handleDownload = (rows?: any[]) => {
        const selRows = rows ?? [];
        setSelectedRows(selRows);
        props.downloadReports(selRows);
    };

    return (
        <React.Fragment>
            {props.productSales.length > 0 ? <CclDownloadWarningAlert /> : null}
            <CclPersistentDataGrid
                columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
                data={props.productSales}
                loading={props.loading}
                stateSelector={stateSelector}
                userPreferenceId={9}
                getRowIdFn={(row: ProductSaleParticipant) => row.reportId}
                actionCreator={updateProductSaleGridState}
                hideToolbar={false}
                initState={JSON.stringify({
                    sorting: { sortModel: [{ field: "scoreDate", sort: "desc" }] },
                })}
                resultBar={false}
                resultBarText={""}
                miscButtons={
                    isUserProductSaleManger
                        ? [
                              {
                                  id: "download-reports-button",
                                  caption: "Download Assessment(s)",
                                  onClick: handleDownload,
                                  returnSelected: true,
                                  restricted: false,
                                  disable: selectedRows.length === 0,
                                  loadingBtn: true,
                                  isLoading: props.currentEvent === "download",
                              },
                              {
                                  id: "activate-compass-button",
                                  caption: "Activate Compass",
                                  onClick: props.activateCompass,
                                  returnSelected: true,
                                  restricted: false,
                                  disable: selectedRows.length !== 1,
                                  loadingBtn: true,
                                  isLoading: props.currentEvent === "activate",
                              },
                              {
                                  id: "change-facilitator-button",
                                  caption: "Change Facilitator",
                                  onClick: props.changeFacilitator,
                                  returnSelected: true,
                                  restricted: false,
                                  disable: selectedRows.length === 0,
                              },
                          ]
                        : []
                }
                showCheckboxes={isUserProductSaleManger}
                disableSelectionOnClick={isUserProductSaleManger}
                onSelectedRowsChange={(rows) => setSelectedRows(rows as any[])}
                hideFooter={false}
            />
        </React.Fragment>
    );
};

export default ProductSaleAssessmentList;
